import { useCallback, useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { createRingtone } from "@/lib/sound";
import { AUDIO_CONSTRAINTS, tuneOpus, monitorStats } from "@/lib/webrtc";

const FALLBACK_ICE = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

let iceServersPromise = null;
function fetchIceServers() {
  if (!iceServersPromise) {
    iceServersPromise = axios
      .get(`${API}/turn-credentials`)
      .then(({ data }) => (Array.isArray(data?.iceServers) && data.iceServers.length ? data.iceServers : FALLBACK_ICE))
      .catch(() => FALLBACK_ICE);
  }
  return iceServersPromise;
}

// Agent-side global call center: handles incoming calls for ANY conversation in
// the tenant (even with no chat open), a queue of multiple simultaneous
// incoming calls, and a single active 1:1 audio call.
export function useAgentCallCenter({ send, selfName, onAccept }) {
  const [incoming, setIncoming] = useState([]); // [{conversationId, callerName}]
  const [active, setActive] = useState(null); // {conversationId, callerName, state, seconds, muted}

  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const iceServersRef = useRef(FALLBACK_ICE);
  const timerRef = useRef(null);
  const ringingRef = useRef(new Map()); // convId -> {callerName, offer, candidates:[]}
  const ringTimersRef = useRef(new Map()); // convId -> timeoutId (auto-expire stale rings)
  const callingTimeoutRef = useRef(null); // unanswered outgoing call timeout
  const activeRef = useRef(null); // {conversationId, callerName, role}
  const activePendingCandsRef = useRef([]);
  const ringtoneRef = useRef(null);
  const statsStopRef = useRef(null);
  const restartTimerRef = useRef(null);
  const restartsRef = useRef(0);
  const sendRef = useRef(send);
  const onAcceptRef = useRef(onAccept);
  sendRef.current = send;
  onAcceptRef.current = onAccept;

  const startTimer = useCallback(() => {
    clearInterval(timerRef.current);
    setActive((a) => (a ? { ...a, seconds: 0 } : a));
    timerRef.current = setInterval(() => {
      setActive((a) => (a ? { ...a, seconds: a.seconds + 1 } : a));
    }, 1000);
  }, []);

  const teardownActive = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(callingTimeoutRef.current);
    clearTimeout(restartTimerRef.current);
    if (statsStopRef.current) { statsStopRef.current(); statsStopRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((t) => t.stop());
      localStreamRef.current = null;
    }
    if (pcRef.current) {
      pcRef.current.onicecandidate = null;
      pcRef.current.ontrack = null;
      pcRef.current.onconnectionstatechange = null;
      pcRef.current.oniceconnectionstatechange = null;
      try { pcRef.current.close(); } catch {}
      pcRef.current = null;
    }
    if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    activePendingCandsRef.current = [];
    activeRef.current = null;
    restartsRef.current = 0;
    setActive(null);
  }, []);

  const removeIncoming = useCallback((convId) => {
    const t = ringTimersRef.current.get(convId);
    if (t) { clearTimeout(t); ringTimersRef.current.delete(convId); }
    ringingRef.current.delete(convId);
    setIncoming((list) => list.filter((c) => c.conversationId !== convId));
  }, []);

  const setNet = useCallback((convId, patch) => {
    setActive((a) => (a && a.conversationId === convId ? { ...a, ...patch } : a));
  }, []);

  const startStats = useCallback((convId) => {
    if (statsStopRef.current || !pcRef.current) return;
    statsStopRef.current = monitorStats(pcRef.current, ({ level, quality }) => {
      setActive((a) => (a && a.conversationId === convId
        ? { ...a, level, netState: a.netState === "reconnecting" ? "reconnecting" : (quality === "poor" ? "poor" : "good") }
        : a));
    });
  }, []);

  const iceRestart = useCallback(async (convId) => {
    const pc = pcRef.current;
    if (!pc || !activeRef.current || activeRef.current.role !== "caller" || restartsRef.current >= 2) return;
    restartsRef.current += 1;
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      offer.sdp = tuneOpus(offer.sdp);
      await pc.setLocalDescription(offer);
      sendRef.current({ type: "webrtc", conversation_id: convId, kind: "offer", sdp: pc.localDescription });
    } catch {}
  }, []);

  const buildPeer = useCallback(async (convId) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false });
    localStreamRef.current = stream;
    const pc = new RTCPeerConnection({ iceServers: iceServersRef.current });
    stream.getTracks().forEach((t) => pc.addTrack(t, stream));
    pc.onicecandidate = (e) => {
      if (e.candidate) sendRef.current({ type: "webrtc", conversation_id: convId, kind: "candidate", candidate: e.candidate });
    };
    pc.ontrack = (e) => {
      const el = remoteAudioRef.current;
      if (el) {
        el.srcObject = e.streams[0];
        const p = el.play?.();
        if (p && p.catch) p.catch(() => {});
      }
    };
    pc.oniceconnectionstatechange = () => {
      const st = pc.iceConnectionState;
      if (st === "connected" || st === "completed") {
        clearTimeout(restartTimerRef.current);
        restartsRef.current = 0;
        setNet(convId, { netState: "good" });
      } else if (st === "disconnected") {
        setNet(convId, { netState: "reconnecting" });
        clearTimeout(restartTimerRef.current);
        restartTimerRef.current = setTimeout(() => {
          if (pcRef.current && ["disconnected", "failed"].includes(pcRef.current.iceConnectionState)) iceRestart(convId);
        }, 3000);
      } else if (st === "failed") {
        setNet(convId, { netState: "reconnecting" });
        iceRestart(convId);
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "connected") setNet(convId, { netState: "good" });
      if (pc.connectionState === "closed" && activeRef.current) teardownActive();
    };
    pcRef.current = pc;
    return pc;
  }, [teardownActive, setNet, iceRestart]);

  const startCall = useCallback(async (convId, calleeName) => {
    if (!convId || activeRef.current) return;
    try {
      const pc = await buildPeer(convId);
      const offer = await pc.createOffer();
      offer.sdp = tuneOpus(offer.sdp);
      await pc.setLocalDescription(offer);
      activeRef.current = { conversationId: convId, callerName: calleeName, role: "caller" };
      setActive({ conversationId: convId, callerName: calleeName || "Visitor", state: "calling", seconds: 0, muted: false, netState: "connecting", level: 0 });
      sendRef.current({ type: "call-request", conversation_id: convId, offer: pc.localDescription, caller: selfName });
      // Auto-hang up if the callee never answers.
      clearTimeout(callingTimeoutRef.current);
      callingTimeoutRef.current = setTimeout(() => {
        if (activeRef.current && activeRef.current.conversationId === convId && !activeRef.current.connected) {
          try { sendRef.current({ type: "call-end", conversation_id: convId }); } catch {}
          teardownActive();
        }
      }, 45000);
    } catch {
      teardownActive();
      toast.error("Microphone access is required to start a call.");
    }
  }, [buildPeer, selfName, teardownActive]);

  const acceptCall = useCallback(async (convId) => {
    const entry = ringingRef.current.get(convId);
    if (!entry) return;
    // One active call at a time — hang up the current one before answering.
    if (activeRef.current) {
      try { sendRef.current({ type: "call-end", conversation_id: activeRef.current.conversationId }); } catch {}
      teardownActive();
    }
    try {
      const pc = await buildPeer(convId);
      await pc.setRemoteDescription(new RTCSessionDescription(entry.offer));
      for (const c of entry.candidates) {
        try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
      }
      const answer = await pc.createAnswer();
      answer.sdp = tuneOpus(answer.sdp);
      await pc.setLocalDescription(answer);
      sendRef.current({ type: "call-accept", conversation_id: convId, answer: pc.localDescription });
      activeRef.current = { conversationId: convId, callerName: entry.callerName, role: "callee", connected: true };
      setActive({ conversationId: convId, callerName: entry.callerName || "Visitor", state: "connected", seconds: 0, muted: false, netState: "connecting", level: 0 });
      startTimer();
      startStats(convId);
      removeIncoming(convId);
      onAcceptRef.current && onAcceptRef.current(convId);
    } catch {
      teardownActive();
      sendRef.current({ type: "call-reject", conversation_id: convId });
      removeIncoming(convId);
    }
  }, [buildPeer, teardownActive, startTimer, removeIncoming, startStats]);

  const rejectCall = useCallback((convId) => {
    sendRef.current({ type: "call-reject", conversation_id: convId });
    removeIncoming(convId);
  }, [removeIncoming]);

  const endCall = useCallback(() => {
    if (activeRef.current) sendRef.current({ type: "call-end", conversation_id: activeRef.current.conversationId });
    teardownActive();
  }, [teardownActive]);

  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;
    const track = localStreamRef.current.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setActive((a) => (a ? { ...a, muted: !track.enabled } : a));
    }
  }, []);

  const processSignal = useCallback(async (data) => {
    const convId = data.conversation_id;
    if (!convId) return;

    if (data.type === "call-request") {
      if (activeRef.current && activeRef.current.conversationId === convId) return;
      const existing = ringingRef.current.get(convId);
      if (existing) { existing.offer = data.offer; return; } // dedupe
      const callerName = data.caller || data.from_name || "Visitor";
      ringingRef.current.set(convId, { callerName, offer: data.offer, candidates: [] });
      setIncoming((list) => (list.some((c) => c.conversationId === convId) ? list : [...list, { conversationId: convId, callerName }]));
      // Safety net: drop the ring if the caller never reconnects/cancels.
      const t = setTimeout(() => removeIncoming(convId), 45000);
      ringTimersRef.current.set(convId, t);
    } else if (data.type === "call-accept") {
      if (activeRef.current && activeRef.current.conversationId === convId && activeRef.current.role === "caller" && pcRef.current && data.answer) {
        try {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.answer));
          for (const c of activePendingCandsRef.current) {
            try { await pcRef.current.addIceCandidate(new RTCIceCandidate(c)); } catch {}
          }
          activePendingCandsRef.current = [];
          activeRef.current.connected = true;
          clearTimeout(callingTimeoutRef.current);
          setActive((a) => (a && a.conversationId === convId ? { ...a, state: "connected" } : a));
          startTimer();
          startStats(convId);
        } catch {}
      } else if (ringingRef.current.has(convId)) {
        // Another agent answered this ringing call — drop it from our queue.
        removeIncoming(convId);
      }
    } else if (data.type === "call-reject" || data.type === "call-end") {
      if (ringingRef.current.has(convId)) removeIncoming(convId);
      if (activeRef.current && activeRef.current.conversationId === convId) teardownActive();
    } else if (data.type === "webrtc" && data.kind === "candidate") {
      if (activeRef.current && activeRef.current.conversationId === convId) {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) {
          try { await pc.addIceCandidate(new RTCIceCandidate(data.candidate)); } catch {}
        } else {
          activePendingCandsRef.current.push(data.candidate);
        }
      } else {
        const entry = ringingRef.current.get(convId);
        if (entry) entry.candidates.push(data.candidate);
      }
    } else if (data.type === "webrtc" && data.kind === "offer") {
      // ICE-restart renegotiation from the peer — answer it.
      const pc = pcRef.current;
      if (pc && data.sdp && activeRef.current && activeRef.current.conversationId === convId) {
        try {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          const answer = await pc.createAnswer();
          answer.sdp = tuneOpus(answer.sdp);
          await pc.setLocalDescription(answer);
          sendRef.current({ type: "webrtc", conversation_id: convId, kind: "answer", sdp: pc.localDescription });
        } catch {}
      }
    } else if (data.type === "webrtc" && data.kind === "answer") {
      const pc = pcRef.current;
      if (pc && data.sdp && activeRef.current && activeRef.current.conversationId === convId) {
        try { await pc.setRemoteDescription(new RTCSessionDescription(data.sdp)); } catch {}
      }
    }
  }, [startTimer, removeIncoming, teardownActive, startStats]);

  useEffect(() => {
    const el = document.createElement("audio");
    el.autoplay = true;
    el.setAttribute("playsinline", "true");
    el.style.display = "none";
    document.body.appendChild(el);
    remoteAudioRef.current = el;
    ringtoneRef.current = createRingtone();

    let alive = true;
    fetchIceServers().then((s) => { if (alive) iceServersRef.current = s; });

    return () => {
      alive = false;
      ringtoneRef.current && ringtoneRef.current.stop();
      teardownActive();
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const ringing = incoming.length > 0 && !(active && active.state === "connected");
    if (ringing) ringtoneRef.current && ringtoneRef.current.start();
    else ringtoneRef.current && ringtoneRef.current.stop();
  }, [incoming.length, active]);

  return { incoming, active, startCall, acceptCall, rejectCall, endCall, toggleMute, processSignal };
}
